import { useEffect, useRef } from 'react';

import { AccountFieldDefinition } from '@flipdish/api-client-typescript';
import { useFormikContext } from 'formik';
import omit from 'lodash/omit';

export interface PopulatedAccountField {
  Key: string | undefined;
  DisplayName: string | undefined;
  Value: string;
}

export interface BankFormValues {
  PopulatedAccountFields: PopulatedAccountField[];
  BusinessType: string;
  AccountName: string;
  BankName: string;
  BankAddress: string;
  BankCountryCode: string;
}

const COUNTRY_SPECIFIC_FIELDS = ['PopulatedAccountFields', 'BankName', 'BankAddress'];

interface UseBankCountryFieldsOptions {
  /** Field definitions for the selected country, in display order. */
  countryFields: AccountFieldDefinition[];
  /** GB form improvements (CE-1263) active: existing values are matched by DisplayName, not Key. */
  isGbFormImprovements: boolean;
  /** Called after the form is reset for a new country, to reset the caller's own state. */
  onCountryChange: () => void;
}

/**
 * Owns the country-specific part of the bank account form.
 *
 * - Keeps `PopulatedAccountFields` in step with `countryFields`: one entry per field
 *   definition, keeping values already entered when the definitions change for the same
 *   country (e.g. the GB fields being reordered once the feature flag loads).
 * - When `BankCountryCode` changes (CE-1363), nothing from the previous country carries
 *   over: `PopulatedAccountFields` is rebuilt blank (even when both countries share field
 *   keys, e.g. IBAN), the lookup-resolved `BankName` and `BankAddress` are cleared, their
 *   touched/error state is dropped, and `onCountryChange` is called.
 *
 * Everything runs in a single effect, so there is no ordering to get wrong.
 */
export const useBankCountryFields = ({
  countryFields,
  isGbFormImprovements,
  onCountryChange,
}: UseBankCountryFieldsOptions) => {
  const { values, touched, errors, setFieldValue, setTouched, setErrors } =
    useFormikContext<BankFormValues>();
  const countryCodeRef = useRef(values.BankCountryCode);
  const fieldsKeyRef = useRef('');

  useEffect(() => {
    const countryChanged = values.BankCountryCode !== countryCodeRef.current;
    if (countryChanged) {
      countryCodeRef.current = values.BankCountryCode;
      setFieldValue('BankName', '', false);
      setFieldValue('BankAddress', '', false);
      setTouched(omit(touched, COUNTRY_SPECIFIC_FIELDS), false);
      setErrors(omit(errors, COUNTRY_SPECIFIC_FIELDS));
      onCountryChange();
    }

    if (countryFields.length === 0) {
      // No definitions yet: keep any existing values (e.g. edit view initial values), unless
      // they belong to the previous country.
      if (countryChanged) {
        setFieldValue('PopulatedAccountFields', [], false);
      }
      return;
    }

    const fieldsKey = countryFields.map((f) => f.Key).join(',');
    const fieldsChanged = fieldsKey !== fieldsKeyRef.current;
    const populatedFields = values.PopulatedAccountFields || [];
    if (!countryChanged && !fieldsChanged && populatedFields.length > 0) return;

    const existingFields = countryChanged ? [] : populatedFields;
    const initialFields = countryFields.map((fieldDef) => {
      // GB form improvements: match on DisplayName — getDynamicKey() stores Key as a
      // string ('Iban', 'SortCode') while fieldDef.Key from Zeus is numeric (1, 12).
      // Pre-CE-1263 / non-GB: match on Key (master behaviour).
      const existingField = existingFields.find((f) =>
        isGbFormImprovements ? f.DisplayName === fieldDef.DisplayName : f.Key === fieldDef.Key
      );
      return {
        Key: fieldDef.Key,
        DisplayName: fieldDef.DisplayName,
        Value: existingField?.Value || '',
      };
    });
    // Skip validation on a country reset: it would run against a snapshot that may still hold
    // the previous country's values.
    setFieldValue('PopulatedAccountFields', initialFields, countryChanged ? false : undefined);
    fieldsKeyRef.current = fieldsKey;
  }, [
    values.BankCountryCode,
    values.PopulatedAccountFields,
    countryFields,
    isGbFormImprovements,
    touched,
    errors,
    setFieldValue,
    setTouched,
    setErrors,
    onCountryChange,
  ]);
};
